import { createId } from '../utils/id';
import { boundingBoxOf, clampPointToBox, clampBoxToBounds } from '../utils/geometry';
import type { BoundingBox } from '../utils/geometry';
import { ELEMENT_META_BY_TYPE, MIN_ELEMENT_SIZE, SCHEMA_VERSION, defaultShape } from './constants';

export interface ActionLogEntry {
  id: string;
  date: string;
  note: string;
}

export interface ShapeVertex {
  id: string;
  x: number;
  y: number;
}

export interface GardenElement {
  id: string;
  type: string;
  x: number;
  y: number;
  name: string;
  actions: ActionLogEntry[];
  // Only present on resizable (structure) elements; point elements omit these.
  width?: number;
  height?: number;
}

export interface GardenState {
  version: number;
  elements: GardenElement[];
  shape: ShapeVertex[];
}

export interface ElementMove {
  id: string;
  x: number;
  y: number;
}

export type GardenAction =
  | { type: 'LOAD_STATE'; state: GardenState }
  | { type: 'ADD_ELEMENT'; elementType: string; x: number; y: number }
  | { type: 'MOVE_ELEMENT'; id: string; x: number; y: number }
  | { type: 'MOVE_ELEMENTS_BULK'; moves: ElementMove[] }
  | { type: 'RESIZE_ELEMENT'; id: string; width: number; height: number }
  | { type: 'RENAME_ELEMENT'; id: string; name: string }
  | { type: 'DELETE_ELEMENT'; id: string }
  | { type: 'DELETE_ELEMENTS_BULK'; ids: string[] }
  | { type: 'ADD_ACTION_ENTRY'; elementId: string; entry: ActionLogEntry }
  | { type: 'ADD_ACTION_ENTRY_BULK'; elementIds: string[]; date: string; note: string }
  | { type: 'UPDATE_ACTION_ENTRY'; elementId: string; entryId: string; date: string; note: string }
  | { type: 'DELETE_ACTION_ENTRY'; elementId: string; entryId: string }
  | { type: 'UPDATE_SHAPE_POINT'; id: string; x: number; y: number }
  | { type: 'INSERT_SHAPE_POINT'; index: number; point: ShapeVertex }
  | { type: 'REMOVE_SHAPE_POINT'; id: string };

export const initialGardenState: GardenState = {
  version: SCHEMA_VERSION,
  elements: [],
  shape: defaultShape(),
};

function placeElement(
  state: GardenState,
  elementType: string,
  x: number,
  y: number,
): GardenElement {
  const meta = ELEMENT_META_BY_TYPE[elementType];
  const box = boundingBoxOf(state.shape);

  if (meta.resizable) {
    const width = meta.defaultWidth as number;
    const height = meta.defaultHeight as number;
    const position = clampBoxToBounds(x - width / 2, y - height / 2, width, height, box);
    return {
      id: createId(),
      type: elementType,
      x: position.x,
      y: position.y,
      width,
      height,
      name: `New ${meta.label}`,
      actions: [],
    };
  }

  const position = clampPointToBox(x, y, box);
  return {
    id: createId(),
    type: elementType,
    x: position.x,
    y: position.y,
    name: `New ${meta.label}`,
    actions: [],
  };
}

function clampElementPosition(element: GardenElement, x: number, y: number, box: BoundingBox) {
  if (element.width != null && element.height != null) {
    return clampBoxToBounds(x, y, element.width, element.height, box);
  }
  return clampPointToBox(x, y, box);
}

function moveElementInList(
  elements: GardenElement[],
  id: string,
  x: number,
  y: number,
  box: BoundingBox,
): GardenElement[] {
  return elements.map((el) => {
    if (el.id !== id) return el;
    const position = clampElementPosition(el, x, y, box);
    return { ...el, x: position.x, y: position.y };
  });
}

export function gardenReducer(state: GardenState, action: GardenAction): GardenState {
  switch (action.type) {
    case 'LOAD_STATE': {
      return action.state;
    }

    case 'ADD_ELEMENT': {
      const newElement = placeElement(state, action.elementType, action.x, action.y);
      return { ...state, elements: [...state.elements, newElement] };
    }

    case 'MOVE_ELEMENT': {
      const box = boundingBoxOf(state.shape);
      return {
        ...state,
        elements: moveElementInList(state.elements, action.id, action.x, action.y, box),
      };
    }

    case 'MOVE_ELEMENTS_BULK': {
      const box = boundingBoxOf(state.shape);
      let elements = state.elements;
      for (const move of action.moves) {
        elements = moveElementInList(elements, move.id, move.x, move.y, box);
      }
      return { ...state, elements };
    }

    case 'RESIZE_ELEMENT': {
      const { id, width, height } = action;
      const box = boundingBoxOf(state.shape);
      const clampedWidth = Math.max(width, MIN_ELEMENT_SIZE);
      const clampedHeight = Math.max(height, MIN_ELEMENT_SIZE);
      return {
        ...state,
        elements: state.elements.map((el) => {
          if (el.id !== id) return el;
          const position = clampBoxToBounds(el.x, el.y, clampedWidth, clampedHeight, box);
          return { ...el, width: clampedWidth, height: clampedHeight, ...position };
        }),
      };
    }

    case 'RENAME_ELEMENT': {
      const { id, name } = action;
      return {
        ...state,
        elements: state.elements.map((el) => (el.id === id ? { ...el, name } : el)),
      };
    }

    case 'DELETE_ELEMENT': {
      const { id } = action;
      return { ...state, elements: state.elements.filter((el) => el.id !== id) };
    }

    case 'DELETE_ELEMENTS_BULK': {
      const idSet = new Set(action.ids);
      return { ...state, elements: state.elements.filter((el) => !idSet.has(el.id)) };
    }

    case 'ADD_ACTION_ENTRY': {
      const { elementId, entry } = action;
      return {
        ...state,
        elements: state.elements.map((el) =>
          el.id === elementId ? { ...el, actions: [...el.actions, entry] } : el,
        ),
      };
    }

    case 'ADD_ACTION_ENTRY_BULK': {
      const { elementIds, date, note } = action;
      const idSet = new Set(elementIds);
      return {
        ...state,
        elements: state.elements.map((el) =>
          idSet.has(el.id)
            ? { ...el, actions: [...el.actions, { id: createId(), date, note }] }
            : el,
        ),
      };
    }

    case 'UPDATE_ACTION_ENTRY': {
      const { elementId, entryId, date, note } = action;
      return {
        ...state,
        elements: state.elements.map((el) =>
          el.id === elementId
            ? {
                ...el,
                actions: el.actions.map((entry) =>
                  entry.id === entryId ? { ...entry, date, note } : entry,
                ),
              }
            : el,
        ),
      };
    }

    case 'DELETE_ACTION_ENTRY': {
      const { elementId, entryId } = action;
      return {
        ...state,
        elements: state.elements.map((el) =>
          el.id === elementId
            ? { ...el, actions: el.actions.filter((entry) => entry.id !== entryId) }
            : el,
        ),
      };
    }

    case 'UPDATE_SHAPE_POINT': {
      const { id, x, y } = action;
      return {
        ...state,
        shape: state.shape.map((point) => (point.id === id ? { ...point, x, y } : point)),
      };
    }

    case 'INSERT_SHAPE_POINT': {
      const { index, point } = action;
      const shape = [...state.shape];
      shape.splice(index + 1, 0, point);
      return { ...state, shape };
    }

    case 'REMOVE_SHAPE_POINT': {
      if (state.shape.length <= 3) return state;
      return { ...state, shape: state.shape.filter((point) => point.id !== action.id) };
    }

    default:
      return state;
  }
}
